import type {
  CreateAdminRepresentativeDto,
  UpdateAdminRepresentativeDto
} from '@idc-forms/api-client'
import type {
  V2CreateAdminRepresentativeInput,
  V2UpdateAdminRepresentativeInput
} from '@idc-forms/schemas/api-v2/admin-representatives'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { representativesAdminV2Service } from '../services/representatives-admin-v2.service'

/**
 * Mutaciones de representantes legales contra la API v2. Identifican por id público
 * (`rep_`) y, al completar, invalidan todo el árbol `['representatives']` (listado +
 * summary + detalle). El alta/edición vive en el side panel.
 */
function useV2AccessToken() {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

export const useCreateRepresentativeV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    // Se acepta el tipo de DOMINIO (schema Zod) y se castea en el borde del SDK:
    // el generado y el schema describen el mismo dato en runtime pero TS los ve
    // como tipos distintos.
    mutationFn: async (body: V2CreateAdminRepresentativeInput) =>
      await representativesAdminV2Service.create(accessToken, body as CreateAdminRepresentativeDto),
    onSuccess: () => {
      toast.success('Representante creado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.representatives.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useUpdateRepresentativeV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: V2UpdateAdminRepresentativeInput }) =>
      await representativesAdminV2Service.update(
        accessToken,
        id,
        body as UpdateAdminRepresentativeDto
      ),
    onSuccess: () => {
      toast.success('Representante actualizado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.representatives.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useDeleteRepresentativeV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async (id: string) => await representativesAdminV2Service.remove(accessToken, id),
    onSuccess: () => {
      toast.success('Representante eliminado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.representatives.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}
